import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that displays a currency selector form.
 * Simplified version of localization-form for currency selection only.
 *
 * @typedef {object} CurrencySelectorRefs
 * @property {HTMLDivElement} currencyList - The currency list element.
 * @property {HTMLInputElement} countryInput - The country input element (for form submission).
 * @property {HTMLUListElement[]} currencyListItems - The currency list items element.
 * @property {HTMLFormElement} form - The form element.
 *
 * @extends {Component<CurrencySelectorRefs>}
 */
class CurrencySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.currencyList && this.refs.currencyList.addEventListener('keydown', this.#onContainerKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.currencyList && this.refs.currencyList.removeEventListener('keydown', this.#onContainerKeyDown);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, currencyListItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCurrencyFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCurrencyFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = currencyListItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? '';
          form.submit();
        }
        break;
      }
    }
  };

  /**
   * Selects a currency.
   *
   * @param {string} countryCode - The ISO code of the country to select (for the currency).
   * @param {Event} event - The event object.
   */
  selectCurrency = (countryCode, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;

    countryInput.value = countryCode;
    form?.submit();
  };

  /**
   * Changes the currency focus.
   *
   * @param {'UP' | 'DOWN'} direction - The direction to change focus.
   */
  #changeCurrencyFocus(direction) {
    const focusableItems = this.refs.currencyListItems.filter((item) => !item.hasAttribute('hidden'));

    if (!focusableItems || focusableItems.length === 0) return;

    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);

    // Clear previous selections
    for (const item of focusableItems) {
      item.setAttribute('aria-selected', 'false');
    }

    let indexToFocus = 0;

    if (focusedItemIndex === -1) {
      indexToFocus = direction === 'DOWN' ? 0 : focusableItems.length - 1;
    } else {
      switch (direction) {
        case 'UP':
          indexToFocus = focusedItemIndex === 0 ? focusableItems.length - 1 : focusedItemIndex - 1;
          break;
        case 'DOWN':
          indexToFocus = focusedItemIndex === focusableItems.length - 1 ? 0 : focusedItemIndex + 1;
          break;
      }
    }

    const itemToFocus = focusableItems[indexToFocus];

    if (itemToFocus) {
      itemToFocus.setAttribute('aria-selected', 'true');
      itemToFocus.focus();

      const listContainer = this.refs.currencyList;
      if (listContainer) {
        const itemTop = itemToFocus.offsetTop;
        const itemBottom = itemTop + itemToFocus.offsetHeight;
        const containerScrollTop = listContainer.scrollTop;
        const containerHeight = listContainer.clientHeight;

        if (itemBottom > containerScrollTop + containerHeight) {
          listContainer.scrollTop = itemBottom - containerHeight;
        } else if (itemTop < containerScrollTop) {
          listContainer.scrollTop = itemTop;
        }
      }
    }
  }
}

/**
 * A custom element that displays a dropdown currency selector.
 *
 * @typedef {object} DropdownCurrencyRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {CurrencySelectorComponent} currencySelector - The currency selector component.
 *
 * @extends {Component<DropdownCurrencyRefs>}
 */
class DropdownCurrencySelectorComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty('--width', `${this.refs.currencySelector.offsetWidth}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus();
        break;
    }
  };
}

/**
 * A custom element that displays a drawer currency selector.
 *
 * @typedef {object} DrawerCurrencyRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {CurrencySelectorComponent} currencySelector - The currency selector component.
 *
 * @extends {Component<DrawerCurrencyRefs>}
 */
class DrawerCurrencySelectorComponent extends Component {
  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;

    if (!(target instanceof HTMLDetailsElement)) return;

    // No additional logic needed for simple currency selector drawer
  }
}

if (!customElements.get('currency-selector-component')) {
  customElements.define('currency-selector-component', CurrencySelectorComponent);
}

if (!customElements.get('dropdown-currency-selector-component')) {
  customElements.define('dropdown-currency-selector-component', DropdownCurrencySelectorComponent);
}

if (!customElements.get('drawer-currency-selector-component')) {
  customElements.define('drawer-currency-selector-component', DrawerCurrencySelectorComponent);
}
